import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table, TableBody, TableCell, TableFooter,
  TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AlertCircle, BarChart3, ChevronDown, ChevronUp } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { dashboardApi, AiUsageStatus } from '@/api/dashboardApi';
import { Spinner } from '@/components/ui/spinner';
import { AiUsageDailyDetailPanel, DailyDetailTarget } from './AiUsageDailyDetailPanel';

// ── 타입 ────────────────────────────────────────────────────
interface DailyRow {
  date: string;
  total: number;
  uiTotal: number;
  WEBSITE_CRAWLING: number;
  IMAGE_URL: number;
  IMAGE_UPLOAD: number;
  ui_IMAGE_URL?: number;
  ui_IMAGE_UPLOAD?: number;
  openApiTotal: number;
  openApi_IMAGE_URL: number;
  openApi_IMAGE_UPLOAD: number;
  scheduledTotal: number;
  scheduled_WEBSITE_CRAWLING: number;
}

interface GrandTotal extends Omit<DailyRow, 'date'> {}

interface DailySummaryData {
  targetUserId: string | null;
  startDate: string;
  endDate: string;
  rows: DailyRow[];
  grandTotal: GrandTotal;
}

// ── 헬퍼 ────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const currentMonthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function AiUsageDailyPage() {
  const { user, token } = useAuth();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();
  const isSuperAdmin = user?.userRole === 'SUPER_ADMIN';
  const [searchParams] = useSearchParams();

  const [startDate, setStartDate] = useState(() => {
    const paramStart = searchParams.get('startDate');
    if (paramStart) return paramStart;
    return currentMonthStart(); // 기본값 (청구 기간 로드 후 업데이트됨)
  });
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || today());
  const [filterUserId, setFilterUserId] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DailySummaryData | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<{ startDate: string; endDate: string; nextBillingDate: string } | null>(null);

  // OPEN_API 컬럼 접기/펼치기
  const [showOpenApi, setShowOpenApi] = useState(true);
  // 정기크롤링 컬럼 접기/펼치기
  const [showScheduled, setShowScheduled] = useState(true);

  // 개인/팀 조회 체크박스 (팀장만 표시)
  const [isTeamView, setIsTeamView] = useState(false);

  // 일별 상세 이력 드릴다운 (숫자 클릭 시)
  const [detailTarget, setDetailTarget] = useState<DailyDetailTarget | null>(null);

  // 항상 최신 필터 값을 유지하는 ref (fetchData가 stale closure 없이 최신 값을 읽을 수 있게)
  const filtersRef = useRef({ token, startDate, endDate, filterUserId, isSuperAdmin, isTeamView });
  filtersRef.current = { token, startDate, endDate, filterUserId, isSuperAdmin, isTeamView };

  // AI 사용량 상태 (팀 컨텍스트 포함)
  const { data: aiUsageStatus } = useQuery({
    queryKey: ['aiUsageStatus'],
    queryFn: () => dashboardApi.getAiUsageStatus(),
  });

  // 청구 기간 로드
  useEffect(() => {
    const loadBillingPeriod = async () => {
      try {
        const period = await dashboardApi.getBillingPeriod();
        setBillingPeriod(period);
        // searchParams에 startDate가 없을 때만 청구기간 시작일로 설정
        const paramStart = searchParams.get('startDate');
        if (!paramStart && period) {
          setStartDate(period.startDate);
        }
      } catch {
        // 청구 기간 로드 실패 시 무시 (기본값 사용)
      }
    };
    loadBillingPeriod();
  }, []); // searchParams는 처음에만 확인하면 됨

  // startDate가 변경되면 fetchData 자동 호출
  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = useCallback(async () => {
    const { token: tk, startDate: sd, endDate: ed, filterUserId: uid, isSuperAdmin: isAdmin, isTeamView: teamView } = filtersRef.current;
    if (!tk) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (sd) params.append('startDate', sd);
    if (ed) params.append('endDate', ed);
    if (isAdmin && uid.trim()) params.append('userId', uid.trim());
    if (teamView) params.append('teamView', 'true');

    try {
      const res = await fetch(`${BASE}/api/dashboard/ai-usage/daily-summary?${params}`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json.data ?? json);
    } catch (e: any) {
      setError(e.message || '데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []); // filtersRef를 통해 최신 값 읽으므로 deps 불필요

  // 첫 마운트 시 fetchData 호출 (초기 로드)
  useEffect(() => {
    fetchData();
  }, []); // 빈 dependency로 마운트 시 1회만 실행

  // 숫자 클릭 시 해당 (기간 · 형태 · 구분)의 근거 목록을 하단 패널에 표시 (0건은 근거가 없으므로 클릭 비활성화)
  const clickableCell = (v: number, startDate: string, endDate: string, sourceType: string | undefined, channel: string | undefined, label: string, boldWhenNonZero = false) => (
    <TableCell className="text-right">
      {v > 0 ? (
        <button
          type="button"
          onClick={() => setDetailTarget({ startDate, endDate, sourceType, channel, label })}
          className={`hover:underline hover:text-primary focus:outline-none focus:underline text-inherit ${boldWhenNonZero ? 'font-semibold' : ''}`}
        >
          {v.toLocaleString()}
        </button>
      ) : (
        <span className="text-gray-400">0</span>
      )}
    </TableCell>
  );

  const gt = data?.grandTotal;

  return (
    <>
      <PageTitle title="일별 AI 사용량" />
      <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || 'AI 이용 현황'}
        description="날짜별 서비스 이용 건수 집계 (직접생성 + 정기크롤링 + OPEN API)"
        icon={menuIcon}
      />

      {/* 조회 조건 */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <Label>시작일</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label>종료일</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-40"
              />
            </div>
            {isSuperAdmin && (
              <div className="space-y-1">
                <Label>사용자 ID (SUPER_ADMIN)</Label>
                <Input
                  type="text"
                  placeholder="전체 (비워두면 모든 사용자)"
                  value={filterUserId}
                  onChange={e => setFilterUserId(e.target.value)}
                  className="w-56"
                />
              </div>
            )}
            {aiUsageStatus?.isTeamContext && aiUsageStatus?.role === 'OWNER' && (
              <div className="space-y-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="teamView"
                  checked={isTeamView}
                  onChange={e => setIsTeamView(e.target.checked)}
                  className="w-4 h-4"
                />
                <Label htmlFor="teamView" className="cursor-pointer">팀 전체 조회</Label>
              </div>
            )}
            <Button onClick={fetchData} disabled={loading} className="h-10">
              {loading ? <Spinner size="sm" className="mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              조회
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 합계 카드 */}
      {gt && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-blue-600 font-medium mb-1">전체 합계</p>
              <p className="text-2xl font-bold text-blue-800">{gt.total.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              {aiUsageStatus?.isTeamContext && (
                <p className="text-xs text-blue-500 mt-1">
                  {aiUsageStatus.role === 'OWNER' ? '팀 전체' : '본인'} 사용량
                  {aiUsageStatus.role === 'OWNER' && aiUsageStatus.teamTotalUsed !== null && (
                    <span> (팀: {aiUsageStatus.teamTotalUsed.toLocaleString()}건)</span>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="bg-indigo-50 border-indigo-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-indigo-600 font-medium mb-1">직접생성 방식 합계</p>
              <p className="text-2xl font-bold text-indigo-800">{gt.uiTotal.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              <p className="text-xs text-indigo-500 mt-1">크롤링 {gt.WEBSITE_CRAWLING} · URL {gt.IMAGE_URL ?? gt.ui_IMAGE_URL ?? 0} · 업로드 {gt.IMAGE_UPLOAD ?? gt.ui_IMAGE_UPLOAD ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-purple-600 font-medium mb-1">연동생성 합계</p>
              <p className="text-2xl font-bold text-purple-800">{gt.openApiTotal.toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              <p className="text-xs text-purple-500 mt-1">URL {gt.openApi_IMAGE_URL} · 업로드 {gt.openApi_IMAGE_UPLOAD}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-emerald-600 font-medium mb-1">정기크롤링 합계</p>
              <p className="text-2xl font-bold text-emerald-800">{(gt.scheduledTotal ?? 0).toLocaleString()}<span className="text-sm font-normal ml-1">건</span></p>
              <p className="text-xs text-emerald-500 mt-1">웹사이트 {gt.scheduled_WEBSITE_CRAWLING ?? 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-gray-600 font-medium mb-1">조회 기간</p>
              <p className="text-sm font-semibold text-gray-800 break-words">{data?.startDate} ~ {data?.endDate}</p>
            </CardContent>
          </Card>
          {/* 엔터프라이즈 계약 정보 */}
          {aiUsageStatus?.enterpriseContract && (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-purple-600 font-medium mb-1">엔터프라이즈 계약</p>
                <p className="text-sm font-semibold text-purple-800">{aiUsageStatus.enterpriseContract.contractName}</p>
                <p className="text-xs text-purple-500 mt-1">월 한도: {aiUsageStatus.enterpriseContract.monthlyLimit.toLocaleString()}건</p>
                <p className="text-xs text-purple-500">월 이용료: ₩{aiUsageStatus.enterpriseContract.monthlyPrice.toLocaleString()}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* 상세 테이블 */}
      {data && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">일별 상세</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowScheduled(v => !v)}
                  className="text-xs"
                >
                  {showScheduled ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                  정기크롤링 {showScheduled ? '접기' : '펼치기'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOpenApi(v => !v)}
                  className="text-xs"
                >
                  {showOpenApi ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                  OPEN API {showOpenApi ? '접기' : '펼치기'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead rowSpan={2} className="align-middle">날짜</TableHead>
                  <TableHead rowSpan={2} className="text-right align-middle">전체</TableHead>
                  <TableHead colSpan={4} className="text-center text-indigo-600 border-l">직접 합계</TableHead>
                  {showScheduled && (
                    <TableHead colSpan={2} className="text-center text-emerald-600 border-l">정기크롤링 합계</TableHead>
                  )}
                  {showOpenApi && (
                    <TableHead colSpan={3} className="text-center text-purple-600 border-l">연동 합계</TableHead>
                  )}
                </TableRow>
                <TableRow>
                  <TableHead className="text-right text-indigo-600 border-l">소계</TableHead>
                  <TableHead className="text-right text-indigo-500">크롤링</TableHead>
                  <TableHead className="text-right text-indigo-500">이미지 URL</TableHead>
                  <TableHead className="text-right text-indigo-500">이미지 업로드</TableHead>
                  {showScheduled && (
                    <React.Fragment key="scheduled-header">
                      <TableHead className="text-right text-emerald-600 border-l">소계</TableHead>
                      <TableHead className="text-right text-emerald-500">웹사이트</TableHead>
                    </React.Fragment>
                  )}
                  {showOpenApi && (
                    <React.Fragment key="openapi-subheader">
                      <TableHead className="text-right text-purple-600 border-l">소계</TableHead>
                      <TableHead className="text-right text-purple-500">이미지 URL</TableHead>
                      <TableHead className="text-right text-purple-500">이미지 업로드</TableHead>
                    </React.Fragment>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6 + (showScheduled ? 2 : 0) + (showOpenApi ? 3 : 0)}
                      className="text-center text-gray-400 py-10"
                    >
                      조회된 데이터가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.rows.map((row) => (
                    <TableRow key={row.date}>
                      <TableCell className="font-mono">{row.date}</TableCell>
                      {clickableCell(row.total, row.date, row.date, undefined, undefined, '전체')}
                      {clickableCell(row.uiTotal, row.date, row.date, undefined, 'INNER', '직접 합계')}
                      {clickableCell(row.WEBSITE_CRAWLING, row.date, row.date, 'WEBSITE_CRAWLING', undefined, '웹사이트 크롤링')}
                      {clickableCell(row.IMAGE_URL ?? row.ui_IMAGE_URL ?? 0, row.date, row.date, 'IMAGE_URL', undefined, '이미지 URL')}
                      {clickableCell(row.IMAGE_UPLOAD ?? row.ui_IMAGE_UPLOAD ?? 0, row.date, row.date, 'IMAGE_UPLOAD', undefined, '이미지 업로드')}
                      {showScheduled && (
                        <React.Fragment key="scheduled-row">
                          {clickableCell(row.scheduledTotal, row.date, row.date, undefined, 'SCHEDULED', '정기크롤링 합계')}
                          {clickableCell(row.scheduled_WEBSITE_CRAWLING, row.date, row.date, 'WEBSITE_CRAWLING', 'SCHEDULED', '정기크롤링 웹사이트')}
                        </React.Fragment>
                      )}
                      {showOpenApi && (
                        <React.Fragment key="openapi-row">
                          {clickableCell(row.openApiTotal, row.date, row.date, undefined, 'OPEN_API', '연동 합계')}
                          {clickableCell(row.openApi_IMAGE_URL, row.date, row.date, 'IMAGE_URL', 'OPEN_API', '연동 이미지 URL')}
                          {clickableCell(row.openApi_IMAGE_UPLOAD, row.date, row.date, 'IMAGE_UPLOAD', 'OPEN_API', '연동 이미지 업로드')}
                        </React.Fragment>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
              {gt && data && (
                <TableFooter>
                  <TableRow>
                    <TableCell className="font-semibold">합계</TableCell>
                    {clickableCell(gt.total, data.startDate, data.endDate, undefined, undefined, '전체(조회기간 합계)', true)}
                    {clickableCell(gt.uiTotal, data.startDate, data.endDate, undefined, 'INNER', '직접 합계(조회기간 합계)', true)}
                    {clickableCell(gt.WEBSITE_CRAWLING, data.startDate, data.endDate, 'WEBSITE_CRAWLING', undefined, '웹사이트 크롤링(조회기간 합계)')}
                    {clickableCell(gt.IMAGE_URL ?? gt.ui_IMAGE_URL ?? 0, data.startDate, data.endDate, 'IMAGE_URL', undefined, '이미지 URL(조회기간 합계)')}
                    {clickableCell(gt.IMAGE_UPLOAD ?? gt.ui_IMAGE_UPLOAD ?? 0, data.startDate, data.endDate, 'IMAGE_UPLOAD', undefined, '이미지 업로드(조회기간 합계)')}
                    {showScheduled && (
                      <React.Fragment key="scheduled-footer">
                        {clickableCell(gt.scheduledTotal, data.startDate, data.endDate, undefined, 'SCHEDULED', '정기크롤링 합계(조회기간 합계)', true)}
                        {clickableCell(gt.scheduled_WEBSITE_CRAWLING, data.startDate, data.endDate, 'WEBSITE_CRAWLING', 'SCHEDULED', '정기크롤링 웹사이트(조회기간 합계)')}
                      </React.Fragment>
                    )}
                    {showOpenApi && (
                      <React.Fragment key="openapi-footer">
                        {clickableCell(gt.openApiTotal, data.startDate, data.endDate, undefined, 'OPEN_API', '연동 합계(조회기간 합계)', true)}
                        {clickableCell(gt.openApi_IMAGE_URL, data.startDate, data.endDate, 'IMAGE_URL', 'OPEN_API', '연동 이미지 URL(조회기간 합계)')}
                        {clickableCell(gt.openApi_IMAGE_UPLOAD, data.startDate, data.endDate, 'IMAGE_UPLOAD', 'OPEN_API', '연동 이미지 업로드(조회기간 합계)')}
                      </React.Fragment>
                    )}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {detailTarget && (
        <AiUsageDailyDetailPanel
          target={detailTarget}
          onClose={() => setDetailTarget(null)}
          filterUserId={filterUserId}
          isSuperAdmin={isSuperAdmin}
          isTeamView={isTeamView}
        />
      )}
    </PageContainer>
    </>
  );
}
